import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { Tooltip } from 'react-tooltip';
import 'photoswipe/dist/photoswipe.css';
import styles from '../../styles/case-study-modal.module.scss';

const MetaIcon = ({ type }) => {
  if (type === 'calendar') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
        <g fill="none" stroke="var(--color-primary-accent-5)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <rect width="20" height="18" x="2" y="4" rx="4" />
          <path d="M8 2v4m8-4v4M2 10h20" />
        </g>
      </svg>
    );
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
      <g fill="none" stroke="var(--color-primary-accent-5)" stroke-width="1">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 8h4" />
        <path stroke-width="1.7" d="M20.833 9h-2.602C16.446 9 15 10.343 15 12s1.447 3 3.23 3h2.603c.084 0 .125 0 .16-.002c.54-.033.97-.432 1.005-.933c.002-.032.002-.071.002-.148v-3.834c0-.077 0-.116-.002-.148c-.036-.501-.465-.9-1.005-.933C20.959 9 20.918 9 20.834 9Z" />
        <path stroke-width="1.7" d="M20.965 9c-.078-1.872-.328-3.02-1.137-3.828C18.657 4 16.771 4 13 4h-3C6.229 4 4.343 4 3.172 5.172S2 8.229 2 12s0 5.657 1.172 6.828S6.229 20 10 20h3c3.771 0 5.657 0 6.828-1.172c.809-.808 1.06-1.956 1.137-3.828" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.991 12h.01" />
      </g>
    </svg>
  );
};

/**
 * @param {{
 * images: string[],
 * title: string,
 * caseData?: {
 * title?: string,
 * serviceType?: string,
 * timeline?: string,
 * priceRange?: string,
 * hidePrice?: boolean
 * } | null
 * }} props
 */
export default function CaseStudySlider({ images, title, caseData }) {
  const viewportRef = useRef(null);
  const [showViewportFade, setShowViewportFade] = useState(false);
  const [imageSizes, setImageSizes] = useState({});

  const updateViewportFade = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      setShowViewportFade(false);
      return;
    }

    const hasOverflow = viewport.scrollHeight - viewport.clientHeight > 1;
    const isAtBottom = viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 1;
    setShowViewportFade(hasOverflow && !isAtBottom);
  }, []);

  useEffect(() => {
    updateViewportFade();
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const rafId = requestAnimationFrame(() => {
      updateViewportFade();
    });

    const content = viewport.querySelector('[data-media-scroll-content]');
    const resizeObserver = new ResizeObserver(() => {
      updateViewportFade();
    });
    resizeObserver.observe(viewport);
    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [images, updateViewportFade]);

  useEffect(() => {
    let isCancelled = false;

    images.forEach((src) => {
      const probe = new window.Image();
      probe.onload = () => {
        if (isCancelled || !probe.naturalWidth || !probe.naturalHeight) {
          return;
        }
        setImageSizes((prev) => {
          const prevSize = prev[src];
          if (prevSize?.width === probe.naturalWidth && prevSize?.height === probe.naturalHeight) {
            return prev;
          }
          return {
            ...prev,
            [src]: {
              width: probe.naturalWidth,
              height: probe.naturalHeight,
            },
          };
        });
      };
      probe.src = src;
    });

    return () => {
      isCancelled = true;
    };
  }, [images]);

  const handleImageLoad = useCallback(
    (src, event) => {
      const img = event.currentTarget;
      if (img.naturalWidth && img.naturalHeight) {
        setImageSizes((prev) => {
          const prevSize = prev[src];
          if (prevSize?.width === img.naturalWidth && prevSize?.height === img.naturalHeight) {
            return prev;
          }
          return {
            ...prev,
            [src]: {
              width: img.naturalWidth,
              height: img.naturalHeight,
            },
          };
        });
      }
      updateViewportFade();
    },
    [updateViewportFade],
  );

  return (
    <div className={styles.sliderCard}>
      <header className={styles.head}>
        <h3>{caseData?.title || 'Название кейса'}</h3>
        <span className={styles.serviceBadge}>{caseData?.serviceType || 'Лендинг + WordPress'}</span>
      </header>

      <div className={styles.metaList}>
        <span className={styles.metaBadge}>
          <MetaIcon type="calendar" />
          {caseData?.timeline || '8 дней'}
        </span>
        {!caseData?.hidePrice ? (
          <span className={styles.metaBadge} data-tooltip-id="case-study-price-tooltip" data-tooltip-content="Стоимость похожего проекта" data-tooltip-place="top">
            <MetaIcon type="wallet" />
            {caseData?.priceRange || '45 000–70 000 ₽'}
          </span>
        ) : null}
      </div>

      <div className={`${styles.mediaViewportWrap} ${showViewportFade ? styles.mediaViewportWithFade : ''}`}>
        <div ref={viewportRef} className={styles.mediaViewport} onScroll={updateViewportFade}>
          {!images.length ? (
            <div className={styles.slidePlaceholder}>Скриншоты кейса появятся по запросу</div>
          ) : (
            <Gallery>
              <div className={styles.mediaScroll} aria-label="Галерея кейса" data-media-scroll-content>
                {images.map((src, index) => (
                  <div className={styles.slide} key={`${src}-${index}`}>
                    <Item original={src} thumbnail={src} width={imageSizes[src]?.width || 1200} height={imageSizes[src]?.height || 1200}>
                      {({ ref, open }) => (
                        <button type="button" className={styles.slideImageButton} onClick={open} aria-label={`Открыть изображение ${index + 1}`}>
                          <img ref={ref} className={styles.slideImage} src={src} alt={`${title} — экран ${index + 1}`} loading="lazy" onLoad={(event) => handleImageLoad(src, event)} />
                        </button>
                      )}
                    </Item>
                  </div>
                ))}
              </div>
            </Gallery>
          )}
        </div>
      </div>
      {!caseData?.hidePrice ? <Tooltip id="case-study-price-tooltip" place="top" className="myTooltip" /> : null}
    </div>
  );
}
